import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import CheckoutModal from '../components/CheckoutModal';
import ReviewModal from '../components/ReviewModal';
import WhatsAppConfirmModal from '../components/WhatsAppConfirmModal';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Home = () => {
  const { isOpen: cartOpen, setIsOpen: setCartOpen } = useCart();
  const { settings } = useSettings();
  const [homeBanner, setHomeBanner] = useState(null);
  const [comboBanner, setComboBanner] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/banners`),
      axios.get(`${API}/categories`),
      axios.get(`${API}/products?active=true&featured=true`),
    ]).then(([bannerRes, catRes, prodRes]) => {
      const banners = bannerRes.data;
      setHomeBanner(banners.find(b => b.type === 'home'));
      setComboBanner(banners.find(b => b.type === 'combo'));
      setCategories(catRes.data);
      setFeatured(prodRes.data.slice(0, 10));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleOrderPlaced = (order) => {
    setCheckoutOpen(false);
    setPendingOrder(order);
  };

  const heroImg = homeBanner?.image || 'https://images.unsplash.com/photo-1568051243858-533a607809a5?w=1600&q=85&auto=format&fit=crop';

  return (
    <div className="min-h-screen bg-cream" data-testid="home-page">
      <SEO
        title="Fresh Cakes, Pastries & Snacks in Sawai Madhopur"
        description="Order freshly baked cakes, pastries, snacks and custom celebration cakes from Apni Dhani Bakery, Sawai Madhopur. Fast delivery and pickup available."
        path="/"
      />
      <Navbar onCartClick={() => setCartOpen(true)} />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center"
        style={{ background: `url(${heroImg}) center/cover no-repeat` }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#4A0F1E]/95 via-[#4A0F1E]/85 to-[#4A0F1E]/50 sm:bg-gradient-to-r sm:from-[#4A0F1E]/90 sm:via-[#4A0F1E]/60 sm:to-transparent" />
        <div className="absolute inset-0 arch-pattern opacity-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-24 pb-16 sm:pb-24">
          <div className="max-w-xl">
            <p className="text-[#C86A7A] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 animate-fade-in">
              Freshly Baked, Just for You
            </p>
            <h1 className="font-playfair text-3xl sm:text-5xl lg:text-7xl font-black text-white leading-tight mb-4 break-words animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Delicious Moments,<br />
              <span className="text-[#C86A7A]">Baked with Love</span>
            </h1>
            <p className="text-white/75 text-base md:text-lg max-w-md mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              From our oven to your heart. Discover handcrafted cakes, fresh pastries, tasty snacks and more.
            </p>
            <div className="flex flex-wrap gap-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Link
                to="/menu"
                className="flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
                data-testid="hero-order-now"
              >
                Order Now
              </Link>
              <Link
                to="/menu"
                className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold px-7 py-3.5 rounded-full transition-colors border border-white/30"
                data-testid="hero-view-menu"
              >
                View Menu <ArrowRight size={16} />
              </Link>
              <a
                href={`https://wa.me/${(settings.whatsappNumber || '919001204207').replace(/\D/g, '')}?text=${encodeURIComponent('Hey, I want a customized cake for an event')}`}
                target="_blank"
                rel="noreferrer"
                className="cake-cta-pulse flex items-center gap-2 bg-gradient-to-r from-[#C86A7A] to-[#7A1125] text-white font-semibold px-7 py-3.5 rounded-full transition-transform hover:scale-105"
                data-testid="hero-customised-cake-button"
              >
                <Sparkles size={16} /> Get Your Customised Cake
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ──────────────────────────────────────── */}
      <section className="bg-white border-y border-[#ECE0DA]" data-testid="stats-strip">
        <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-3 gap-4 md:gap-8">
          {[
            { label: '100% Fresh', sub: 'Premium Ingredients', icon: '🌿' },
            { label: 'Freshly Baked', sub: 'Made Every Day', icon: '🧁' },
            { label: '5000+ Happy', sub: 'Customers', icon: '😊' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-2xl mb-1">{s.icon}</div>
              <div className="font-playfair font-bold text-[#1F1410] text-sm md:text-base">{s.label}</div>
              <div className="text-[#7A6A63] text-xs">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SHOP BY CATEGORY ─────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="categories-section">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-playfair text-3xl font-bold text-[#1F1410]">Shop by Category</h2>
            <div className="h-0.5 w-16 bg-[#7A1125] mt-2 rounded" />
          </div>
          <Link to="/menu" className="flex items-center gap-1 text-[#7A1125] text-sm font-semibold hover:gap-2 transition-all" data-testid="view-all-categories">
            View All <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3">
          {loading ? (
            Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-2xl border border-[#ECE0DA] bg-white animate-pulse">
                <div className="w-14 h-14 rounded-xl bg-[#ECE0DA]" />
                <div className="h-3 w-12 bg-[#ECE0DA] rounded" />
              </div>
            ))
          ) : (
            categories.map(cat => (
              <Link
                key={cat._id}
                to={`/menu?category=${encodeURIComponent(cat.name)}`}
                className="group flex flex-col items-center gap-2 p-3 rounded-2xl border border-[#ECE0DA] bg-white hover:border-[#7A1125] hover:shadow-md transition-all duration-200"
                data-testid={`category-${cat.name}`}
              >
                <div className="w-14 h-14 rounded-xl overflow-hidden">
                  <img
                    src={cat.image || `https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=80`}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    onError={e => { e.target.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=80'; }}
                  />
                </div>
                <span className="text-xs font-semibold text-[#1F1410] text-center leading-tight">{cat.name}</span>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* ── TRENDING NOW ─────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-4 pb-16" data-testid="trending-section">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <h2 className="font-playfair text-3xl font-bold text-[#1F1410]">Trending Now</h2>
            <Flame size={24} className="text-[#7A1125]" />
          </div>
          <Link to="/menu" className="flex items-center gap-1 text-[#7A1125] text-sm font-semibold hover:gap-2 transition-all" data-testid="view-all-products">
            View All <ArrowRight size={14} />
          </Link>
        </div>

        <div className="relative">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-[#ECE0DA] bg-white overflow-hidden animate-pulse">
                  <div className="w-full aspect-square bg-[#ECE0DA]" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 w-3/4 bg-[#ECE0DA] rounded" />
                    <div className="h-3 w-1/2 bg-[#ECE0DA] rounded" />
                  </div>
                </div>
              ))
            ) : (
              featured.slice(slideIndex, slideIndex + 5).map(p => (
                <ProductCard key={p._id} product={p} />
              ))
            )}
          </div>
          {featured.length > 5 && (
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={() => setSlideIndex(Math.max(0, slideIndex - 5))}
                disabled={slideIndex === 0}
                className="p-2 rounded-full border border-[#ECE0DA] hover:border-[#7A1125] disabled:opacity-30 transition-colors"
                data-testid="trending-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setSlideIndex(Math.min(featured.length - 5, slideIndex + 5))}
                disabled={slideIndex + 5 >= featured.length}
                className="p-2 rounded-full border border-[#ECE0DA] hover:border-[#7A1125] disabled:opacity-30 transition-colors"
                data-testid="trending-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── COMBO PROMO BANNER ───────────────────────────────── */}
      {comboBanner && (
        <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="combo-banner">
          <div
            className="relative rounded-3xl overflow-hidden min-h-[200px] flex items-center"
            style={{ background: comboBanner.image ? `url(${comboBanner.image}) center/cover` : '#4A0F1E' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#4A0F1E]/95 via-[#4A0F1E]/80 to-transparent" />
            <div className="relative z-10 px-8 md:px-12 py-8 max-w-lg">
              <span className="bg-[#7A1125] text-white text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">
                Limited Time Offer
              </span>
              <h3 className="font-playfair text-white text-2xl md:text-3xl font-bold mb-2">{comboBanner.headline}</h3>
              <p className="text-white/70 text-sm mb-4">{comboBanner.subtext}</p>
              <Link
                to="/combos"
                className="inline-flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold px-6 py-2.5 rounded-full transition-colors"
                data-testid="combo-banner-cta"
              >
                {comboBanner.ctaText || 'Order Combo'} <ArrowRight size={14} />
              </Link>
            </div>
            <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center justify-center w-20 h-20 bg-[#7A1125] rounded-full border-4 border-white/20">
              <span className="text-white text-xs font-bold text-center leading-tight">Save<br/>Up to<br/>20%</span>
            </div>
          </div>
        </section>
      )}

      <Footer />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }} />
      <CheckoutModal isOpen={checkoutOpen} onClose={() => setCheckoutOpen(false)} onOrderPlaced={handleOrderPlaced} />
      {pendingOrder && <WhatsAppConfirmModal order={pendingOrder} onClose={() => { setPlacedOrder(pendingOrder); setPendingOrder(null); }} />}
      {placedOrder && <ReviewModal order={placedOrder} onClose={() => setPlacedOrder(null)} />}
    </div>
  );
};

export default Home;
